// src/components/DealGrid.jsx
import React, { useRef, useMemo, memo, useState } from 'react';
import { PamGrid, BooleanRenderer } from '@1823-partners/core';
import { Button, Stack, Typography, Box, CircularProgress, useTheme, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { isAdcoGeneratedVector, isAdcoVectorField } from '../utils/adcoDisplayUtils';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

// Epoch ms for a date cell (Date, ISO string, YYYYMMDD, or an HTTP-date string
// like "Mon, 16 Jun 2025 00:00:00 GMT"); null when unparseable.
const dateSortValue = (value) => {
  if (value == null || value === '') return null;
  const str = String(value);
  const parsed = /^\d{8}$/.test(str) ? dayjs.utc(str, 'YYYYMMDD') : dayjs.utc(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

const dateSortComparator = (a, b) => {
  const left = dateSortValue(a);
  const right = dateSortValue(b);
  if (left === right) return 0;
  if (left == null) return -1;
  if (right == null) return 1;
  return left - right;
};

const DealGrid = memo(({
  deals,
  columns,
  onSelectionChanged,
  handleEdit,
  handleClone,
  handleLoad,
  loading = false,
  hideEditClone = false,
  useMobileFallback = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:1200px)');
  const gridRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  const colDefs = useMemo(() => {
    const actionColumn = {
      headerName: 'Actions',
      field: 'actions',
      suppressHeaderMenuButton: true,
      cellRenderer: params => (
        <Stack direction="row" spacing={0.5} sx={{ py: 0.5 }}>
          {!hideEditClone && (
          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => handleEdit(params.data)}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#475569' : 'lightgrey',
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
              minWidth: 24,
              px: 0.5,
              height: 32,
              fontSize: 11,
              lineHeight: 1,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#d0d0d0'
              },
              '& .MuiButton-startIcon': {
                margin: 0
              },
              '& .MuiButton-startIcon>*:nth-of-type(1)': {
                fontSize: 16
              }
            }}
          >
            Edit
          </Button>
          )}
          {!hideEditClone && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={() => handleClone(params.data)}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#475569' : 'lightgrey',
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
              minWidth: 24,
              px: 0.5,
              height: 32,
              fontSize: 11,
              lineHeight: 1,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#d0d0d0'
              },
              '& .MuiButton-startIcon': {
                margin: 0
              },
              '& .MuiButton-startIcon>*:nth-of-type(1)': {
                fontSize: 16
              }
            }}
          >
            Clone
          </Button>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => handleLoad(params.data)}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#475569' : 'lightgrey',
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
              minWidth: 24,
              px: 0.5,
              height: 32,
              fontSize: 11,
              lineHeight: 1,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#d0d0d0'
              },
              '& .MuiButton-startIcon': {
                margin: 0
              },
              '& .MuiButton-startIcon>*:nth-of-type(1)': {
                fontSize: 16
              }
            }}
          >
            Load
          </Button>
        </Stack>
      ),
      pinned: 'left',
      width: hideEditClone ? 80 : 200,
      minWidth: hideEditClone ? 80 : 200,
      maxWidth: hideEditClone ? 80 : 200,
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true
    };

    // Show all columns - users can hide/show via AG Grid's column menu
    const final = columns.map(col => {
      if (!col) return null;

      // IMPORTANT: The data object uses headerName as keys (e.g., "Deal Name", "Tranche")
      // NOT the field property (e.g., "DEAL_NAME", "TRANCHE")
      // So we must use headerName as the field for AG Grid
      const fieldKey = col.headerName || col.field;

      // Check if this is a date column
      const isDateColumn = fieldKey === 'ASOFDT' || (fieldKey && fieldKey.toLowerCase().includes('date'));

      // Check if this is a comma-separated column (use text filter for searching within values)
      const isTextSearchColumn = fieldKey === 'CUSIP' || fieldKey === 'Tranche';
      const isAdcoVectorColumn = isAdcoVectorField(fieldKey);

      // Determine filter type
      let filterType;
      if (isDateColumn) {
        filterType = 'agDateColumnFilter';
      } else if (isTextSearchColumn) {
        filterType = 'agTextColumnFilter';
      } else {
        filterType = 'agSetColumnFilter';
      }

      return {
        headerName: col.headerName || col.field,
        field: fieldKey,
        filter: filterType,
        floatingFilter: showFilters,
        ...(isDateColumn && {
          comparator: dateSortComparator,
          filterParams: {
            comparator: (filterDate, cellValue) => {
              if (!cellValue) return -1;

              let cellDate;

              // Handle Date object
              if (cellValue instanceof Date) {
                cellDate = new Date(cellValue);
              }
              // Handle ISO string dates (YYYY-MM-DD)
              else if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                cellDate = new Date(cellValue);
              }
              // Handle numeric dates (YYYYMMDD format)
              else if (typeof cellValue === 'number' || (typeof cellValue === 'string' && cellValue.match(/^\d{8}$/))) {
                const str = String(cellValue);
                if (str.length === 8) {
                  const year = parseInt(str.slice(0, 4));
                  const month = parseInt(str.slice(4, 6)) - 1;
                  const day = parseInt(str.slice(6, 8));
                  cellDate = new Date(year, month, day);
                }
              }
              // Handle other date strings
              else if (typeof cellValue === 'string') {
                cellDate = new Date(cellValue);
              }

              if (!cellDate || isNaN(cellDate.getTime())) return 0;

              cellDate.setHours(0, 0, 0, 0);
              filterDate.setHours(0, 0, 0, 0);

              if (cellDate < filterDate) return -1;
              if (cellDate > filterDate) return 1;
              return 0;
            }
          }
        }),
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 150,
        maxWidth: 300,
        ...(isAdcoVectorColumn && {
          cellRenderer: params => {
            if (!isAdcoGeneratedVector(fieldKey, params.value, params.data)) {
              return params.valueFormatted ?? params.value ?? '';
            }

            return (
              <Tooltip
                title={params.value}
                arrow
                placement="top"
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 640,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      fontFamily: 'monospace',
                      fontSize: 11,
                    },
                  },
                }}
              >
                <Box
                  component="span"
                  tabIndex={0}
                  sx={{
                    color: 'info.main',
                    fontWeight: 700,
                    borderBottom: '1px dotted',
                    cursor: 'help',
                  }}
                >
                  ADCO Generated
                </Box>
              </Tooltip>
            );
          },
        }),
        valueFormatter: ({ value }) => {
          if (value == null) return '';

          // Handle date columns (ASOFDT, MODIFIED_DATE, etc.)
          // Backend dates are calendar dates at midnight UTC/GMT; parse and format
          // in UTC so they aren't shifted back a day in negative-offset timezones.
          if (isDateColumn) {
            // Try parsing as Date object
            if (value instanceof Date) {
              return dayjs.utc(value).format('MM/DD/YYYY');
            }

            // Handle string dates in ISO format (YYYY-MM-DD)
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
              return dayjs.utc(value).format('MM/DD/YYYY');
            }

            // Handle numeric dates (YYYYMMDD format)
            if (typeof value === 'number' || (typeof value === 'string' && value.match(/^\d{8}$/))) {
              const str = String(value);
              if (str.length === 8) {
                return dayjs(str, 'YYYYMMDD').format('MM/DD/YYYY');
              }
            }

            // Handle date strings (like "Mon, 16 Jun 2025 00:00:00 GMT")
            if (typeof value === 'string') {
              const parsed = dayjs.utc(value);
              if (parsed.isValid()) {
                return parsed.format('MM/DD/YYYY');
              }
            }

            // Fallback: try to parse any value as a date
            const parsed = dayjs.utc(value);
            if (parsed.isValid()) {
              return parsed.format('MM/DD/YYYY');
            }
          }

          if (typeof value === 'boolean') return BooleanRenderer({ value });
          if (typeof value === 'number') return value.toLocaleString();
          return value;
        }
      };
    }).filter(Boolean);
    return [actionColumn, ...final];
  }, [columns, handleEdit, handleClone, handleLoad, showFilters, hideEditClone, theme.palette.mode]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 150,
    maxWidth: 300,
    editable: false,
    menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab']
  }), []);

  if (isMobile && useMobileFallback) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">
          Please use the mobile view to see deals.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Toggle Buttons Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mb: 1,
          px: 1,
          pt: 0,
          mt: -1
        }}
      >
        <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              bgcolor: showFilters ? (theme.palette.mode === 'dark' ? '#3b82f6' : '#2563eb') : 'transparent',
              color: showFilters ? 'white' : (theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b'),
              border: `1px solid ${theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1'}`,
              '&:hover': {
                bgcolor: showFilters ? (theme.palette.mode === 'dark' ? '#2563eb' : '#1d4ed8') : (theme.palette.mode === 'dark' ? '#334155' : '#f1f5f9')
              }
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={showColumnSelector ? "Hide Column Selector" : "Show Column Selector"}>
          <IconButton
            size="small"
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            sx={{
              bgcolor: showColumnSelector ? (theme.palette.mode === 'dark' ? '#3b82f6' : '#2563eb') : 'transparent',
              color: showColumnSelector ? 'white' : (theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b'),
              border: `1px solid ${theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1'}`,
              '&:hover': {
                bgcolor: showColumnSelector ? (theme.palette.mode === 'dark' ? '#2563eb' : '#1d4ed8') : (theme.palette.mode === 'dark' ? '#334155' : '#f1f5f9')
              }
            }}
          >
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <PamGrid
          ref={gridRef}
          rowData={deals}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          height="100%"
          showQuickFilter={false}
          showFooter={false}
          showToolbar={false}
          showExport={false}
          containerSx={{ height: '100%' }}
          gridContainerSx={{ height: '100%' }}
          rowHeight={48}
          headerHeight={42}
          rowSelection="multiple"
          sideBar={showColumnSelector ? {
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                }
              }
            ],
            defaultToolPanel: 'columns'
          } : false}
          onGridReady={params => {
            // Auto-size columns to fit
            setTimeout(() => {
              params.api.sizeColumnsToFit();
            }, 100);
          }}
          onSelectionChanged={(params) => {
            const selected = params.api.getSelectedRows();
            onSelectionChanged(selected);
          }}
          suppressRowClickSelection={true}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
          animateRows={false}
        />
      </Box>
    </Box>
  );
});

DealGrid.displayName = 'DealGrid';

export default DealGrid;
